import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Activity, Bot, Coins, Cpu, ReceiptText, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/apiClient";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const STATUS_STYLES = {
  ok: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  error: "bg-destructive/10 text-destructive border-destructive/20",
  rate_limited: "bg-amber-500/10 text-amber-600 border-amber-500/20",
};

const STATUS_LABELS = {
  ok: "OK",
  error: "Error",
  rate_limited: "Rate limit",
};

const fmtNum = (v) => Number(v || 0).toLocaleString("id-ID");

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

// Format tanggal lokal (bukan toISOString) agar tidak bergeser karena timezone.
const pad2 = (n) => String(n).padStart(2, "0");
const toLocalISO = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const monthRange = () => {
  const now = new Date();
  return {
    from: toLocalISO(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: toLocalISO(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
  };
};

export default function LlmUsage() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  // Default rentang: bulan berjalan (hari pertama s/d hari terakhir).
  const [from, setFrom] = useState(() => monthRange().from);
  const [to, setTo] = useState(() => monthRange().to);
  const [businessId, setBusinessId] = useState("all");

  const params = {
    from: from || undefined,
    to: to || undefined,
    business_id: businessId !== "all" ? businessId : undefined,
  };

  const load = async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        api.get("/admin/llm-usage/summary", { params }),
        api.get("/admin/llm-usage", { params: { ...params, limit: 300 } }),
      ]);
      setSummary(s.data);
      setRows(r.data);
    } catch (e) {
      toast.error("Gagal memuat data usage");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/businesses").then((r) => setBusinesses(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [from, to, businessId]); // eslint-disable-line

  const clearUsage = async () => {
    try {
      const { data } = await api.post("/admin/llm-usage/clear", null, {
        params: { business_id: businessId !== "all" ? businessId : undefined },
      });
      toast.success(`${data.deleted} baris usage dihapus`);
      load();
    } catch (e) {
      toast.error("Gagal menghapus data usage");
    }
  };

  const totals = summary || {
    requests: 0, ok: 0, error: 0, rate_limited: 0, total_tokens: 0,
    prompt_tokens: 0, completion_tokens: 0, thought_tokens: 0,
    cost_idr: 0, charge_idr: 0, avg_tokens_per_request: 0,
  };
  // Persen margin dari backend (env LLM_USAGE_MARGIN_PCT) agar label tidak hardcode.
  const marginPct = totals.margin_pct ?? 50;

  const CARDS = [
    {
      key: "requests", icon: Bot, value: fmtNum(totals.requests), color: "#2563EB",
      label: "Pertanyaan ke AI",
      sub: `${totals.ok} OK · ${totals.error} error · ${totals.rate_limited} rate limit`,
    },
    {
      key: "tokens", icon: Cpu, value: fmtNum(totals.total_tokens), color: "#7C3AED",
      label: "Total token",
      sub: `in ${fmtNum(totals.prompt_tokens)} · out ${fmtNum(totals.completion_tokens)} · think ${fmtNum(totals.thought_tokens)}`,
    },
    {
      key: "cost", icon: Coins, value: `Rp${fmtNum(Math.round(totals.cost_idr))}`, color: "#059669",
      label: "Estimasi biaya",
      sub: "Tarif per model (llm_providers.py)",
    },
    {
      key: "charge", icon: ReceiptText, value: `Rp${fmtNum(Math.round(totals.charge_idr))}`, color: "#0EA5E9",
      label: "Estimasi Biaya Final",
      sub: `Biaya dasar + margin ${marginPct}% (charge klien)`,
    },
    {
      key: "avg", icon: Activity, value: fmtNum(totals.avg_tokens_per_request), color: "#D97706",
      label: "Rata-rata token / pertanyaan",
      sub: "total token ÷ jumlah pertanyaan",
    },
  ];

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-border bg-card p-4 grid gap-3 md:grid-cols-[auto_auto_1fr_auto_auto] items-end">
        <div className="space-y-1.5">
          <label className="text-xs text-muted-foreground">Dari</label>
          <Input data-testid="usage-filter-from" type="date" value={from}
            onChange={(e) => setFrom(e.target.value)} className="w-36" />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs text-muted-foreground">Sampai</label>
          <Input data-testid="usage-filter-to" type="date" value={to}
            onChange={(e) => setTo(e.target.value)} className="w-36" />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs text-muted-foreground">Bisnis</label>
          <Select value={businessId} onValueChange={setBusinessId}>
            <SelectTrigger data-testid="usage-filter-business" className="w-full md:w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Bisnis</SelectItem>
              {businesses.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="icon" onClick={load} data-testid="usage-refresh">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" size="icon" data-testid="usage-clear" className="text-destructive">
              <Trash2 className="w-4 h-4" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Hapus data usage?</AlertDialogTitle>
              <AlertDialogDescription>
                {businessId === "all"
                  ? "Seluruh riwayat pemakaian token akan dihapus permanen."
                  : "Riwayat pemakaian token untuk bisnis terpilih akan dihapus permanen."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Batal</AlertDialogCancel>
              <AlertDialogAction onClick={clearUsage} className="bg-destructive text-white">Hapus</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
        {CARDS.map((c, i) => (
          <motion.div key={c.key} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="rounded-2xl border border-border bg-card p-5" data-testid={`usage-stat-${c.key}`}>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3" style={{ background: c.color + "1a" }}>
              <c.icon className="w-4 h-4" style={{ color: c.color }} />
            </div>
            <p className="font-display text-2xl font-bold tracking-tight">{c.value}</p>
            <p className="text-xs text-muted-foreground mt-1">{c.label}</p>
            <p className="text-[11px] text-muted-foreground mt-1">{c.sub}</p>
          </motion.div>
        ))}
      </div>

      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Activity className="w-4 h-4" /> Menampilkan {rows.length} permintaan AI terbaru
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground p-8 text-center" data-testid="usage-empty">
            {loading
              ? "Memuat..."
              : "Belum ada data usage. Data terisi otomatis saat chatbot menjawab lewat AI (balasan FAQ/statis tidak dicatat)."}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="usage-table">
              <TableHeader><TableRow>
                <TableHead>Waktu</TableHead>
                <TableHead>Bisnis</TableHead>
                <TableHead>Model</TableHead>
                <TableHead>Token (in/out/think)</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Biaya</TableHead>
                <TableHead>Margin ({marginPct}%)</TableHead>
                <TableHead>Latensi</TableHead>
                <TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r, i) => (
                  <motion.tr key={r.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    transition={{ delay: Math.min(i * 0.01, 0.3) }}
                    className="border-b border-border last:border-0" data-testid="usage-row">
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(r.created_at)}</TableCell>
                    <TableCell className="text-sm">{r.business_name}</TableCell>
                    <TableCell className="text-xs font-mono">{r.model}</TableCell>
                    <TableCell className="text-xs font-mono whitespace-nowrap">
                      {fmtNum(r.prompt_tokens)} / {fmtNum(r.completion_tokens)} / {fmtNum(r.thought_tokens)}
                    </TableCell>
                    <TableCell className="text-sm font-semibold">{fmtNum(r.total_tokens)}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">Rp{fmtNum(Math.round(r.cost_idr))}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap"
                      title={`Biaya dasar + margin ${marginPct}% (charge ke klien)`}>
                      Rp{fmtNum(Math.round(r.charge_idr))}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {r.latency_ms != null ? `${fmtNum(r.latency_ms)} ms` : "-"}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={STATUS_STYLES[r.status] || "bg-muted text-muted-foreground"}>
                        {STATUS_LABELS[r.status] || r.status || "-"}
                      </Badge>
                    </TableCell>
                  </motion.tr>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
